import { Link } from 'react-router-dom';
import { ArrowRight, Check, MapPinned, Building2, TreePine, Truck, Globe } from 'lucide-react';
import { COVERAGE_AREAS } from '../data/content';
import SEO from '../components/SEO';
import { Reveal } from '../hooks/useReveal';

const ICONS = {
  'london-boroughs': Building2,
  'greater-london': MapPinned,
  'home-counties': TreePine,
  'long-distance-moves': Truck,
  'nationwide-relocations': Globe,
};

export default function ServiceAreas() {
  return (
    <>
      <SEO title="Service Areas — London & Nationwide UK Removals | Laraé Logistics"
        description="Professional removals across all London boroughs, Greater London, the Home Counties, plus long-distance and nationwide UK relocations." />

      <section className="bg-forest text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <Reveal>
            <p className="font-manrope text-sm text-oat uppercase tracking-widest mb-3">Where we cover</p>
            <h1 className="font-outfit text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight max-w-3xl">Serving London &amp; the UK.</h1>
            <p className="font-manrope text-base md:text-lg text-white/80 mt-5 max-w-2xl">
              We provide professional removals services throughout London, Greater London and nationwide destinations across the UK.
            </p>
          </Reveal>
          <Reveal delay={150}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-10 max-w-4xl">
              {['All London Boroughs', 'Greater London', 'Home Counties', 'Long Distance Moves', 'Nationwide Relocations'].map(t => (
                <div key={t} className="flex items-center gap-2 text-sm font-manrope text-white/90">
                  <Check size={16} className="text-oat shrink-0" /> {t}
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-8">
              <Link to="/quote" className="inline-flex items-center gap-2 bg-oat text-ink font-semibold rounded-full px-7 py-3.5 hover:bg-oat/90 hover:translate-x-0.5 transition-all" data-testid="coverage-hero-cta">
                Get My Free Quote <ArrowRight size={18} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <Reveal>
            <div className="max-w-2xl mb-12">
              <p className="font-manrope text-sm font-medium text-forest uppercase tracking-widest mb-3">Coverage</p>
              <h2 className="font-outfit text-3xl md:text-4xl font-medium text-ink tracking-tight">Five coverage areas. One trusted team.</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {COVERAGE_AREAS.map((c, i) => {
              const Icon = ICONS[c.slug] || MapPinned;
              return (
                <Reveal key={c.slug} delay={i * 80}>
                  <Link to={`/coverage/${c.slug}`} className="group block h-full bg-white border border-sand rounded-2xl p-7 hover:border-forest hover:shadow-md hover:-translate-y-0.5 transition-all">
                    <div className="w-12 h-12 rounded-xl bg-sand grid place-items-center text-forest mb-5 group-hover:bg-forest group-hover:text-white transition-colors">
                      <Icon size={22} />
                    </div>
                    <h3 className="font-outfit text-xl font-semibold text-ink">{c.title}</h3>
                    <p className="font-manrope text-sm text-ink/65 mt-2 leading-relaxed">{c.short}</p>
                    <div className="flex items-center gap-1.5 text-forest font-manrope text-sm font-medium mt-5 group-hover:gap-2.5 transition-all">
                      Learn more <ArrowRight size={14} />
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
